import { ErrorMessage } from "./error-message";
import { ReactNode } from "react";
import { cn } from "@verse_core/shared_components/ClassName";

type FieldLayoutProps = {
  id: string;
  label: string;
  error?: string | null;
  // Grey help text under the box; the error replaces it.
  hint?: string;
  // Label to the left of the box, as in a details row; otherwise above it.
  inline?: boolean;
  children: ReactNode;
};

/** A rebrand field's label, its box (`children`) and the error or hint under
 * the box. */
export function FieldLayout({
  id,
  label,
  error,
  hint,
  inline = false,
  children,
}: FieldLayoutProps) {
  return (
    <div
      className={cn(
        "tw-flex tw-w-full",
        inline
          ? "tw-items-start tw-gap-[16px] 720:tw-flex-col 720:tw-gap-[4px]"
          : "tw-flex-col tw-gap-[4px]",
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          // font-normal: the provider app bolds every label.
          "tw-m-0 tw-font-sohne tw-text-[14px] tw-font-normal tw-leading-[1.4]",
          inline
            ? "tw-w-[160px] tw-shrink-0 tw-pt-[8px] tw-text-verse-text-tertiary 720:tw-pt-0"
            : "tw-text-verse-text-primary",
        )}
      >
        {label}
      </label>
      <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-[4px]">
        {children}
        {error ? (
          <ErrorMessage id={`${id}-error`}>{error}</ErrorMessage>
        ) : (
          hint && (
            <p
              id={`${id}-hint`}
              className="tw-m-0 tw-font-sohne tw-text-[12px] tw-leading-[1.4] tw-text-verse-text-tertiary"
            >
              {hint}
            </p>
          )
        )}
      </div>
    </div>
  );
}

/** Border and fill for a rebrand field's box, red when it has an error. */
export const fieldClassName = (error: string | null | undefined) =>
  cn(
    "tw-h-[36px] tw-w-full tw-rounded-[4px] tw-border tw-border-solid tw-p-[10px] tw-font-sohne tw-text-[14px] tw-leading-[1.4] tw-text-verse-text-primary tw-outline-none placeholder:tw-text-verse-text-disabled",
    error
      ? "tw-border-verse-border-critical tw-bg-verse-button-critical"
      : "tw-border-verse-border-secondary tw-bg-white",
  );

type InputFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  // Shown under the field, and turns the field red, when set.
  error?: string | null;
  hint?: string;
  inline?: boolean;
  type?: "text" | "email" | "tel" | "password";
  placeholder?: string;
  inputMode?: "numeric" | "tel" | "email";
  autoComplete?: string;
  // Extra classes for the input box only, e.g. a narrower width than the label.
  inputClassName?: string;
  // A small button at the right of the box, e.g. a password's show/hide.
  trailing?: ReactNode;
};

/** Rebrand text field with its label and an inline error below
 * ("Inputs/Input Field + Label" in Figma), e.g. on the sign-up steps. */
export function InputField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  hint,
  inline,
  type = "text",
  placeholder,
  inputMode,
  autoComplete,
  inputClassName,
  trailing,
}: InputFieldProps) {
  const input = (
    <input
      id={id}
      type={type}
      inputMode={inputMode}
      autoComplete={autoComplete}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
      className={cn(
        fieldClassName(error),
        trailing && "tw-pr-[36px]",
        inputClassName,
      )}
    />
  );
  return (
    <FieldLayout
      id={id}
      label={label}
      error={error}
      hint={hint}
      inline={inline}
    >
      {trailing ? (
        <div className="tw-relative">
          {input}
          <div className="tw-absolute tw-inset-y-0 tw-right-[10px] tw-flex tw-items-center">
            {trailing}
          </div>
        </div>
      ) : (
        input
      )}
    </FieldLayout>
  );
}
